export interface HeaderItem {
  type: "header";
  label: string;
  key: string;
}

export type FlatListItem<T> = T & { type: "item"; key: string };
export type FlatListGroupItem<T> = HeaderItem | FlatListItem<T>;

type KeyOption = "createdAt" | "updatedAt";

export type GroupLabel =
  | "Today"
  | "Yesterday"
  | "Last 7 days"
  | "Last 30 days"
  | "Older";

export type DateRange = {
  start: Date | null; // inclusive
  end: Date | null; // exclusive
  endInclusive: Date | null; // display only (end - 1 second)
};

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function getDateAnchors(now: Date) {
  const todayStart = startOfDay(now);

  const tomorrowStart = new Date(todayStart);
  tomorrowStart.setDate(todayStart.getDate() + 1);

  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(todayStart.getDate() - 1);

  const last7DaysStart = new Date(todayStart);
  last7DaysStart.setDate(todayStart.getDate() - 6); // includes today (7 days total)

  const last30DaysStart = new Date(todayStart);
  last30DaysStart.setDate(todayStart.getDate() - 29); // includes today (30 days total)

  return {
    todayStart,
    tomorrowStart,
    yesterdayStart,
    last7DaysStart,
    last30DaysStart,
  };
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Groups items into:
 * - Today
 * - Yesterday
 * - Last 7 days (excluding Today/Yesterday)
 * - Last 30 days (excluding Last 7 days)
 * - Older
 */
export function groupItemsByRecentDays<
  T extends { createdAt: Date; updatedAt: Date; id: string },
>(items: T[], key: KeyOption = "createdAt"): FlatListGroupItem<T>[] {
  const now = new Date();
  const { todayStart, yesterdayStart, last7DaysStart, last30DaysStart } =
    getDateAnchors(now);

  const groups: Map<string, T[]> = new Map([
    ["Today", []],
    ["Yesterday", []],
    ["Last 7 days", []],
    ["Last 30 days", []],
    ["Older", []],
  ]);

  items.forEach((item) => {
    const d = new Date(item[key]);

    if (isSameDay(d, todayStart)) {
      groups.get("Today")!.push(item);
    } else if (isSameDay(d, yesterdayStart)) {
      groups.get("Yesterday")!.push(item);
    } else if (d >= last7DaysStart && d < yesterdayStart) {
      groups.get("Last 7 days")!.push(item);
    } else if (d >= last30DaysStart && d < last7DaysStart) {
      groups.get("Last 30 days")!.push(item);
    } else {
      groups.get("Older")!.push(item);
    }
  });

  const order: GroupLabel[] = [
    "Today",
    "Yesterday",
    "Last 7 days",
    "Last 30 days",
    "Older",
  ];

  const result: FlatListGroupItem<T>[] = [];

  order.forEach((label) => {
    const groupItems = groups.get(label);
    if (groupItems && groupItems.length > 0) {
      groupItems.sort(
        (a, b) => new Date(b[key]).getTime() - new Date(a[key]).getTime(),
      );

      result.push({
        type: "header",
        label,
        key: label,
      });

      groupItems.forEach((item) =>
        result.push({ ...item, type: "item", key: item.id }),
      );
    }
  });

  return result;
}
function toInclusiveEnd(endExclusive: Date | null): Date | null {
  if (!endExclusive) return null;
  return new Date(endExclusive.getTime() - 1000);
}

/**
 * Given a group label (e.g. "Today" or "Yesterday"), return the corresponding
 * date range that matches `groupItemsByRecentDays`.
 *
 * Ranges are `[start, end)` (start inclusive, end exclusive).
 */
export function getDateRangeForGroup(
  label: GroupLabel,
  now: Date = new Date(),
): DateRange {
  const {
    todayStart,
    tomorrowStart,
    yesterdayStart,
    last7DaysStart,
    last30DaysStart,
  } = getDateAnchors(now);

  switch (label) {
    case "Today":
      return {
        start: todayStart,
        end: tomorrowStart,
        endInclusive: toInclusiveEnd(tomorrowStart),
      };

    case "Yesterday":
      return {
        start: yesterdayStart,
        end: todayStart,
        endInclusive: toInclusiveEnd(todayStart),
      };

    case "Last 7 days":
      return {
        start: last7DaysStart,
        end: yesterdayStart,
        endInclusive: toInclusiveEnd(yesterdayStart),
      };

    case "Last 30 days":
      return {
        start: last30DaysStart,
        end: last7DaysStart,
        endInclusive: toInclusiveEnd(last7DaysStart),
      };

    default:
      return { start: null, end: null, endInclusive: null };
  }
}
